<template>
  <div
    v-if="loaded"
    class="settings-section"
    data-testid="peg-overrides-section"
  >
    <h4 class="section-title">{{ t('Peg por franquia') }}</h4>
    <p class="section-subtitle">
      {{ t('Sobrescreve o peg global para uma franquia. Menor peg = créditos queimam mais rápido. Revogar fecha a janela — o histórico não é apagado.') }}
    </p>

    <div class="settings-toolbar">
      <Button
        :label="t('Definir peg da franquia')"
        icon="pi pi-plus"
        size="small"
        severity="primary"
        data-testid="peg-override-new-btn"
        @click="handleNew"
      />
    </div>

    <data-table
      :config="datatableConfig"
      :data="pegOverrides"
      :refreshButton="{ show: true }"
      data-testid="peg-overrides-table"
      @refresh="fetchOverrides"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from '../../../ui/AppButton.vue'
import { eventBus } from '../../../eventBus'
import { useAiSettings } from '../../../composables/useAiSettings'
import { useConfirmAction } from '../../../composables/useConfirmAction'
import { permissionsService } from '../../../services/permissionsService'
import { log } from '../../../utils/DebugLogger'
import type { AiCredits_PegOverrideRow } from '../../../contracts/AiCredits_PegOverrideRow'

/**
 * Per-franchise peg overrides — the burn-rate lever, listed and created here.
 *
 * This REPLACED PricingOverridesTable's role as the per-franchise control. That
 * table sat over ai_credit_pricing_overrides, which overrode a franchise's
 * PROVIDER COST — a fact about the model, not a per-customer setting, and
 * falsifying it corrupted COGS in every report that read it (2026-08-21). The
 * peg is the honest lever: it changes what a credit is worth to that franchise
 * without lying about what it cost us.
 *
 * Only rows currently IN FORCE are listed. Closed windows are not shown here —
 * they are kept in the database (the table is insert-only) and reachable per
 * franchise, because they are the evidence for burns priced under them.
 */
const { t } = useI18n()
const { confirmAction } = useConfirmAction()
const { pegOverrides, loadPegOverrides, revokePegOverride } = useAiSettings()

const loaded = ref(false)

async function fetchOverrides(): Promise<void> {
  try {
    await loadPegOverrides()
  } catch (err) {
    log('PegOverridesTable', 'fetchOverrides', 'Erro ao carregar peg overrides', err)
  } finally {
    loaded.value = true
  }
}

function handleNew(): void {
  eventBus.emit('modal.pegOverride.open', {})
}

/** Opening from a row pre-selects that franchise — a new window replaces the old. */
function handleEditFranchise(row: AiCredits_PegOverrideRow): void {
  eventBus.emit('modal.pegOverride.open', { franquiaId: row.franquia_id })
}

function handleRevoke(row: AiCredits_PegOverrideRow): void {
  confirmAction({
    message: t('Revogar o peg desta franquia? Ela volta a seguir o peg global a partir de agora. A janela é fechada — o registro histórico não é apagado.'),
    header: t('Revogar peg da franquia'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Revogar'),
    rejectLabel: t('Cancelar'),
    onAccept: async (): Promise<void> => {
      try {
        await revokePegOverride(row.id)
        eventBus.emit('globalMessage.success', t('Peg da franquia revogado com sucesso'))
        await fetchOverrides()
      } catch (err) {
        log('PegOverridesTable', 'handleRevoke', 'Erro ao revogar peg override', err)
        eventBus.emit('globalMessage.error', t('Erro ao revogar o peg da franquia'))
      }
    },
  })
}

function handleButtonClick({ data, action }: { data?: AiCredits_PegOverrideRow; action?: string }): void {
  if (!data) return
  if (action === 'revoke') handleRevoke(data)
  if (action === 'edit') handleEditFranchise(data)
}

/**
 * A raw peg is unreadable on a roster. The burn column translates it into the
 * unit an operator actually reasons in: how many credits a typical prompt costs
 * this franchise.
 */
function burnLabel(peg: unknown): string {
  const value = Number(peg)
  if (!Number.isFinite(value) || value <= 0) return '—'
  const credits = 0.0004 / value
  return credits >= 100
    ? `~${Math.round(credits).toLocaleString('pt-BR')} ${t('créditos')}`
    : `~${credits.toFixed(2)} ${t('créditos')}`
}

const tableColumns = computed(() => [
  {
    data: 'franquia_id',
    title: t('Franquia'),
    cellRenderer: ({ value }: { value: unknown }) =>
      permissionsService.getFranchiseName(value as number) || `#${value as string}`,
  },
  { data: 'peg_usd', title: t('Peg (USD/crédito)') },
  {
    data: 'peg_usd',
    title: t('Prompt típico'),
    cellRenderer: ({ value }: { value: unknown }) => burnLabel(value),
  },
  { data: 'valid_from', title: t('Em vigor desde'), renderType: 'datetime' },
  { data: 'reason', title: t('Motivo') },
  {
    data: null,
    title: t('Ações'),
    renderType: 'action_buttons',
    buttons: [
      {
        text: t('Alterar'),
        icon: 'pi pi-pencil',
        class: 'p-button-secondary p-button-sm',
        action: 'edit',
        testId: (row: Record<string, unknown>) => `peg-override-edit-${row.id}`,
      },
      {
        text: t('Revogar'),
        icon: 'pi pi-ban',
        class: 'p-button-danger p-button-sm',
        action: 'revoke',
        testId: (row: Record<string, unknown>) => `peg-override-revoke-${row.id}`,
      },
    ],
  },
])

const datatableConfig = computed(() => ({
  tableId: 'pegOverridesTable',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 25,
}))

onMounted(() => {
  fetchOverrides()
  eventBus.on('pegOverride.saved', fetchOverrides)
})

onBeforeUnmount(() => {
  eventBus.off('pegOverride.saved', fetchOverrides)
})

defineExpose({ fetchOverrides })
</script>

<style scoped>
.settings-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.section-title {
  margin: 0 0 4px;
}

.section-subtitle {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.settings-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
</style>
